// screens/RecordReading.tsx — C1 (2026-09-28): the reading list — unread
// first, a Show read toggle, receipts verbatim from the server. Root shares
// RadarHome's own `.record` scroll container (RecordHome.css).
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getRecordReadingList,
  markRecordFlagRead,
  type RecordReadingListItemApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import './RecordHome.css';
import './RecordReading.css';

export interface RecordReadingItemTarget {
  kind: RecordReadingListItemApi['kind'];
  id: string;
  slug?: string | null;
}

export interface RecordReadingProps {
  tenantId: string;
  onBack: () => void;
  onOpenItem: (item: RecordReadingItemTarget) => void;
}

type LoadState = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready' };

const KIND_LABEL: Record<string, string> = { note: 'page', file: 'file', decision: 'decision' };

export function RecordReading({ tenantId, onBack, onOpenItem }: RecordReadingProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [items, setItems] = useState<RecordReadingListItemApi[]>([]);
  const [showRead, setShowRead] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<Record<string, string>>({});

  function refresh(includeRead: boolean) {
    return getRecordReadingList(tenantId, includeRead)
      .then((r) => {
        setItems(r);
        setLoad({ phase: 'ready' });
      })
      .catch((err: unknown) => setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the record is not reachable' }));
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh(showRead);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, showRead]);

  async function markRead(item: RecordReadingListItemApi) {
    setBusy(item.flagId);
    try {
      const r = await markRecordFlagRead(tenantId, item.flagId);
      setReceipts((cur) => ({ ...cur, [item.flagId]: r.receipt }));
      setItems((cur) => cur.map((i) => (i.flagId === item.flagId ? { ...i, readAt: new Date().toISOString() } : i)));
    } catch (err) {
      setReceipts((cur) => ({ ...cur, [item.flagId]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setBusy(null);
    }
  }

  const sorted = [...items].sort((a, b) => {
    const aUnread = a.readAt ? 1 : 0;
    const bUnread = b.readAt ? 1 : 0;
    return aUnread - bUnread;
  });

  return (
    <div class="record">
      <BackLink label="Record" onClick={onBack} />
      <div class="record__eyebrow">Reading list</div>

      <button type="button" class="record__strip-btn rreading__toggle" onClick={() => setShowRead((s) => !s)}>
        {showRead ? 'Hide read' : 'Show read'}
      </button>

      {load.phase === 'loading' ? (
        <div class="record__say">One moment…</div>
      ) : load.phase === 'error' ? (
        <div class="record__say">
          The record is not reachable.
          <br />
          <span class="record__say-soft">{load.message}</span>
        </div>
      ) : sorted.length === 0 ? (
        <div class="record__empty">Nothing flagged for you.</div>
      ) : (
        sorted.map((item) => (
          <div class="record__row" key={item.flagId} style={{ cursor: 'default' }}>
            <span class="record__row-top">
              <span class="record__chip">{KIND_LABEL[item.kind] ?? item.kind}</span>
              <button
                type="button"
                class="rreading__title"
                onClick={() => onOpenItem({ kind: item.kind, id: item.id, slug: item.slug })}
              >
                {item.title}
              </button>
            </span>
            <span class="record__row-sub">
              flagged by {item.flaggedBy}
              {item.why ? ` · ${item.why}` : ''}
            </span>
            {receipts[item.flagId] ? (
              <span class="record__row-sub rreading__receipt">{receipts[item.flagId]}</span>
            ) : item.readAt ? (
              <span class="record__row-sub rreading__receipt">read</span>
            ) : (
              <button type="button" class="rreading__link" disabled={busy === item.flagId} onClick={() => void markRead(item)}>
                Mark read
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}
